import { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Grid2 from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import HomeIcon from '@mui/icons-material/Home';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  Certification,
  CertificationProperty,
  CertHouseholdData,
} from 'src/types/Certification';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import CertificationCard from './CertificationCard';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import OnBehalfOfBanner from 'src/components/NewRenterInterface/shared/OnBehalfOfBanner';

interface HouseholdItem {
  household: CertHouseholdData;
  property?: CertificationProperty;
  certId: number;
  categoryName?: string;
  created?: string;
}

/**
 * Presentational list of household certification cards. Supports an optional
 * `emptyState` prop to override the default "No households found" body with a
 * caller-provided node (e.g. a profile-survey CTA card).
 */
interface HouseholdsListProps {
  households: HouseholdItem[];
  onSelectHousehold: (household: CertHouseholdData) => void;
  title?: string;
  description?: string;
  headerIcon?: ReactNode;
  /** Optional content rendered between the PageHeader and the cert grid. */
  headerContent?: ReactNode;
  /** When provided, clicking the AvatarGroup opens HouseholdCompositionModal for that cert. */
  onAvatarGroupClick?: (cert: Certification) => void;
  /**
   * Optional override for the empty state body. When provided and
   * `households.length === 0`, this node is rendered instead of the default
   * "No active certifications found" message. Use this to inject a custom
   * call-to-action (e.g. a profile-survey card) without modifying this
   * list's layout.
   */
  emptyState?: ReactNode;
  /**
   * When set, the matching cert's card renders in a loading state (spinner
   * replaces the chevron, click is disabled). Used by RenterHomePage while
   * resolving the next URL via fetchContinueCertification.
   */
  loadingCertId?: number | null;
  /**
   * When set, an OnBehalfOfBanner ("You are acting on behalf of {name}") is
   * rendered above the cert grid. Pass null/undefined to hide.
   */
  onBehalfOfName?: string | null;
  /**
   * Controls the visual style of the OnBehalfOfBanner.
   * - "banner" (default): full-width bar flush to the header
   * - "card": rounded card that blends into the page content area
   */
  onBehalfOfVariant?: 'banner' | 'card';
  /** Called when the user taps "Continue" on the LockedByPropertyModal. */
  onLockedContinue?: (household: CertHouseholdData) => void;
  /** Optional banner rendered between the header area and the cert grid. */
  banner?: ReactNode;
}

const HouseholdsList: FC<HouseholdsListProps> = ({
  households,
  onSelectHousehold,
  title = '',
  description = '',
  headerIcon,
  headerContent,
  onAvatarGroupClick,
  emptyState,
  loadingCertId,
  onBehalfOfName,
  onBehalfOfVariant = 'banner',
  onLockedContinue,
  banner,
}) => {
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[] | undefined;

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <PageHeader
        title={title}
        icon={headerIcon}
        subtitle={description || undefined}
      />
      {headerContent && <Box sx={{ px: 3, mt: -1 }}>{headerContent}</Box>}
      <OnBehalfOfBanner
        name={onBehalfOfName ?? null}
        variant={onBehalfOfVariant}
      />
      {banner}
      {households.length === 0 ? (
        emptyState ?? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              py: 8,
              textAlign: 'center',
              ...fadeStyle,
            }}
          >
            <HomeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No active applications or re-certifications found
            </Typography>
          </Box>
        )
      ) : (
        <Stack gap={2} sx={{ p: 3, ...fadeStyle }}>
          <Grid2 container spacing={2}>
            {households
              .filter(({ certId }) =>
                certifications?.some((c) => c.id === certId)
              )
              .map(({ household, certId }) => {
                const cert = certifications?.find((c) => c.id === certId);
                if (!cert) {
                  return null;
                }
                return (
                  <Grid2
                    size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
                    key={household.id}
                    sx={{ display: 'flex' }}
                  >
                    <CertificationCard
                      cert={cert}
                      onClick={onSelectHousehold}
                      onAvatarGroupClick={onAvatarGroupClick}
                      isLoading={loadingCertId === cert.id}
                      onLockedContinue={onLockedContinue}
                    />
                  </Grid2>
                );
              })}
          </Grid2>
        </Stack>
      )}
    </Box>
  );
};

export default HouseholdsList;
